import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { Col, Container, Modal as BootstrapModal, Row, Spinner } from 'react-bootstrap'
import { INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION } from '../../../../../../../common/errorMessages'
import { useAppDispatch, useAppSelector } from '../../../../../../../store/hooks'
import { RoomSession } from '../../../../../../../models/Room'
import { TrackEvent, useTracking } from '../../../../common/tracking'
import { selectMeetingInfoHTML } from '../../../../../../../store/meetingSummary/roomSession'
import { useGetAdditionalObjectsQuery, useGetIntegrationUsersQuery, useSaveMeetingMutation } from '../../../../../../../store/integrations/integrations'
import { useGetAccountQuery } from '../../../../../../../store/accountUserSettings/accountUserSettings'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AccountIntegrationConfigMappingType, getAccountCRMIntegrationConfig, OperationType } from '../../../../../../../models/Account'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Modal } from '../../../../../../../components/Modal/Modal'
import { $getRoot, $insertNodes, EditorState, LexicalEditor } from 'lexical'
import { SelectDropdown, SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { getContactOptions } from '../../../../../../../views/MeetingSummary/CRM/utils'
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { WritableLexicalEditor } from '../../../MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import { LEXICAL_NODES } from '../../../MeetingResults/Lexical/nodes'
import { RichTextToolbarPlugin } from '../../../MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { useMeetingSummaryElementsCopy } from '../CopyButton/hooks'
import { HTML_TYPE } from '../../../../../../../common/copy'
import { useExportConfirmation } from '../../../../../../../components/ConfirmationDialog/useExportConfirmation'
import { UserSelectDropdown } from '../../../../../../../views/MeetingSummary/CRM/UserDropdown'
import { TeamSelectDropdown } from '../../../../../../../views/MeetingSummary/CRM/TeamDropdown'
import { selectMeetingSummaryAgentAiAgentResultId, setMeetingSummary } from '../../../../../../../store/meetingSummaryAgent/meetingSummaryAgent'
import { useLazyGetAiAgentResultQuery } from '../../../../../../../store/ai/aiAgentResult'
import { AIAgentType } from '../../../../../../../models/AIAgent'
import { selectSelectedMeetingTemplate } from '../../../../../../../store/meetingSummary/meetingTemplates'
import { IntegrationSavedModalBody } from '../../../../../../../components/Integrations/IntegrationSavedModalBody/IntegrationSavedModalBody'
import { useSyncStatuses } from '../../../ExternalUpdates/hooks'
import { addToast } from '../../../../../../../store/toast/toastSlice'
import { defaultIntegrationContactObject, defaultIntegrationMeetingExportObject, IntegrationServiceId } from '../../../../../../../common/integrations'
import { AdditionalObjects } from '../../../../../../../models/Integration'
import { ContactDropdown } from '../../../../../../../views/MeetingSummary/CRM/ContactDropdown'

const INVALID_INTEGRATION_ERROR_MESSAGES = [INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION]
const DEFAULT_ACCOUNT_CONFIG_MAPPING_SESSION = 'note'

interface CRMSessionObjectMapping {
  [key: string]: string
}

const CRM_SESSION_OBJECT: CRMSessionObjectMapping = {
  wealthbox: 'events',
  redtail: 'activities',
  salesforce: 'Event',
  practifi: 'Event',
  xlr8: 'Event',
  zoho: 'Events',
  elements: 'Event',
  blackdiamond: 'Event',
}

type MappingConfig = {
  path: string
  idKey: string
  labelKey: string
  household: HouseholdEventConfig | undefined
}

type HouseholdEventConfig = {
  householdPath: string
  eventPath: string
}

const eventConfigMap: Record<string, MappingConfig> = {
  wealthbox: { path: 'events', idKey: 'id', labelKey: 'title', household: undefined },
  redtail: { path: 'activities', idKey: 'id', labelKey: 'subject', household: undefined },
  salesforce: {
    path: 'events',
    idKey: 'Id',
    labelKey: 'Subject',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  practifi: {
    path: 'events',
    idKey: 'Id',
    labelKey: 'Subject',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  xlr8: {
    path: 'events',
    idKey: 'Id',
    labelKey: 'Subject',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  zoho: {
    path: 'events',
    idKey: 'id',
    labelKey: 'Event_Title',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  elements: {
    path: 'events',
    idKey: 'Id',
    labelKey: 'Subject',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  blackdiamond: {
    path: 'events',
    idKey: 'Id',
    labelKey: 'Subject',
    household: { householdPath: 'household', eventPath: 'events' },
  },
}

const extractEvents = (data: AdditionalObjects, config: MappingConfig): SelectOption[] => {
  const contactCollection = (data as Record<string, unknown>)?.[config.path]
  const contactEvents: SelectOption[] = Array.isArray(contactCollection)
    ? contactCollection.map((item) => ({
        id: `${item[config.idKey]}`,
        label: `${item[config.labelKey]}`,
      }))
    : []

  const householdEvents: SelectOption[] = config.household
    ? (() => {
        const householdData = (data as Record<string, unknown>)?.[config.household.householdPath] as Record<string, unknown> | undefined
        const householdCollection = householdData?.[config.household.eventPath]
        return Array.isArray(householdCollection)
          ? householdCollection.map((item) => ({
              id: `${item[config.idKey]}`,
              label: `${item[config.labelKey]}`,
            }))
          : []
      })()
    : []

  return [...contactEvents, ...householdEvents].filter((event, index, self) => self.findIndex((e) => e.id === event.id) === index)
}

export interface AddAgentResultToCRMModalProps {
  roomSession: RoomSession
  show: boolean
  onHide: () => void
  serviceId: IntegrationServiceId
  onSave?: () => void
}

type FormValues = {
  externalUserId: string
  notifyUserId: string
  notifyTeamId: string
  initialMeetingDescription: string
  meetingDescription: string
  externalMeetingId: string | null
}

const EVENT_DATE_OFFSET = 7 * 24 * 60 * 60 * 1000

export const AddAgentResultToCRMModal: React.FC<AddAgentResultToCRMModalProps> = ({ show, onHide, roomSession, serviceId, onSave }) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const { tracked, trackingClient } = useTracking()
  const [saveMeeting, { isLoading }] = useSaveMeetingMutation()
  const meetingInfoHTML = useAppSelector(selectMeetingInfoHTML)
  const aiAgentResultId = useAppSelector(selectMeetingSummaryAgentAiAgentResultId)
  const { data: account } = useGetAccountQuery({ accountId })
  const [triggerGetAiAgentResult, { isLoading: isGetAiAgentResultLoading }] = useLazyGetAiAgentResultQuery()
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useExportConfirmation({ roomSession })
  const [savedMeetingResult, setSavedMeetingResult] = useState<boolean>(false)
  const { meetingSummarySyncStatus } = useSyncStatuses()

  const { getCopyContent } = useMeetingSummaryElementsCopy()

  const serviceIntegration = useMemo(() => getAccountCRMIntegrationConfig(account?.data, serviceId), [account, serviceId])

  const sessionMapping = useMemo(() => {
    if (!serviceIntegration) {
      return DEFAULT_ACCOUNT_CONFIG_MAPPING_SESSION
    }

    return typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string'
      ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]
      : (serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]?.objectName ?? 'note')
  }, [serviceIntegration])

  const sessionLabel = useMemo(() => {
    if (!serviceIntegration || !sessionMapping) {
      return DEFAULT_ACCOUNT_CONFIG_MAPPING_SESSION
    }
    if (
      typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string' ||
      !serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]?.displayName
    ) {
      return sessionMapping.charAt(0).toUpperCase() + sessionMapping.slice(1)
    }

    return serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION].displayName
  }, [sessionMapping, serviceIntegration])

  const sessionTitle = useMemo(() => {
    if (!serviceIntegration) {
      return null
    }

    if (typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string') {
      return null
    }

    return (
      (
        serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]?.additionalInformation?.update as {
          title?: string
        }
      )?.title ?? null
    )
  }, [serviceIntegration])

  const isEventAppendSupported = useMemo(() => {
    if (!serviceIntegration) {
      return false
    }
    const updatable =
      typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string'
        ? null
        : Object.keys(serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]?.additionalInformation || {}).includes('updatable')
          ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]?.additionalInformation?.updatable
          : serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]?.operationType
            ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]?.operationType === OperationType.UPDATE
            : null

    return updatable === true || (updatable === null && sessionMapping === CRM_SESSION_OBJECT[serviceId])
  }, [serviceIntegration, serviceId, sessionMapping])

  const contactObject = useMemo(() => {
    if (!serviceIntegration) {
      return defaultIntegrationContactObject[serviceId] || 'Contact'
    }
    return typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT] === 'string'
      ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT]
      : (serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT]?.objectName ?? (defaultIntegrationContactObject[serviceId] || 'Contact'))
  }, [serviceIntegration, serviceId])

  const isNotifySupported = useMemo(() => {
    if (!serviceIntegration) {
      return false
    }
    if (sessionMapping === defaultIntegrationMeetingExportObject[IntegrationServiceId.REDTAIL]?.id && serviceId === IntegrationServiceId.REDTAIL) {
      return true
    }
    return false
  }, [serviceIntegration, serviceId, sessionMapping])

  const initialMeetingDescription = getCopyContent()[HTML_TYPE]

  const defaultValues = {
    externalUserId: '',
    initialMeetingDescription,
    meetingDescription: initialMeetingDescription,
    externalMeetingId: '',
  }

  const _getDescription = (originalDescription: string) => {
    const serviceIntegration = getAccountCRMIntegrationConfig(account?.data, serviceId)
    const accountConfigMappingSession = serviceIntegration
      ? typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string'
        ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]
        : serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]?.objectName
      : DEFAULT_ACCOUNT_CONFIG_MAPPING_SESSION

    if (accountConfigMappingSession === CRM_SESSION_OBJECT[serviceId]) {
      return originalDescription
    }

    return `<div>${meetingInfoHTML} ${originalDescription}</div>`
  }

  const handlePrimaryAction = async () => {
    await handleSubmit(handleSave)()
    if (onSave) {
      onSave()
    }
  }

  const handleSave = tracked(
    async (data: FormValues) => {
      const description = _getDescription(data.meetingDescription)
      try {
        if (data.notifyUserId) {
          trackingClient.track({
            eventName: TrackEvent.NOTIFY_USER_UTILIZED,
            eventDetails: {
              type: 'user',
              serviceId: serviceId,
            },
          })
        }
        if (data.notifyTeamId) {
          trackingClient.track({
            eventName: TrackEvent.NOTIFY_USER_UTILIZED,
            eventDetails: {
              type: 'team',
              serviceId: serviceId,
            },
          })
        }

        await runMutationWithConfirmation(async () => {
          const response = await saveMeeting({
            accountId,
            connector: serviceId,
            body: {
              roomId: roomSession.roomId,
              sessionId: roomSession.id,
              description: description,
              ...(data.notifyUserId ? { notifyUserId: data.notifyUserId } : {}),
              ...(data.notifyTeamId ? { notifyTeamId: data.notifyTeamId } : {}),
              externalUserIds: [data.externalUserId],
              externalMeetingId: data.externalMeetingId,
              relatedTo: { id: data.externalUserId, type: contactObject },
              exportType: 'meetingSummary',
              aiAgentResultId: aiAgentResultId,
            },
          })
          if (!response.error) {
            const existingMeetingSummary = await triggerGetAiAgentResult(
              {
                accountId,
                type: AIAgentType.MEETING_SUMMARY,
                resultKey: `${roomSession.roomId}|${roomSession.id}|${selectedMeetingTemplate?.id}`,
              },
              false,
            ).unwrap()
            if (existingMeetingSummary) {
              dispatch(setMeetingSummary(existingMeetingSummary))
              setSavedMeetingResult(true)
            }
          }
        })
      } catch (e) {
        console.error(e)
      }
    },
    { eventName: TrackEvent.SAVE_MEETING_SUMMARY_TO_CRM },
    {
      eventDetailsExtractor: (data: FormValues) => {
        return {
          roomId: roomSession.roomId,
          sessionId: roomSession.id,
          externalUserId: data.externalUserId,
          connector: serviceId,
        }
      },
    },
  )

  const form = useForm<FormValues>({ defaultValues })
  const { handleSubmit, setValue } = form

  const handleOnHide = useCallback(() => {
    form.reset()
    onHide()
  }, [onHide, form])

  useEffect(() => {
    if (savedMeetingResult && !meetingSummarySyncStatus.length) {
      dispatch(
        addToast({
          variant: 'success',
          title: 'Meeting Summary saved',
          messages: ['Meeting Summary was successfully saved to your CRM'],
        }),
      )
      handleOnHide()
    }
  }, [savedMeetingResult, meetingSummarySyncStatus, handleOnHide, dispatch])

  useEffect(() => {
    setValue('initialMeetingDescription', initialMeetingDescription)
    setValue('meetingDescription', initialMeetingDescription)
  }, [setValue, initialMeetingDescription])

  return (
    <>
      <Modal
        size='xl'
        show={show}
        onHide={handleOnHide}
        header={
          !savedMeetingResult ? (
            <BootstrapModal.Title className='w-100 d-flex align-items-center justify-content-center'>
              Save your meeting to&nbsp;<span className='text-capitalize'>{serviceId}</span>
            </BootstrapModal.Title>
          ) : null
        }
        body={
          <FormProvider {...form}>
            {savedMeetingResult ? (
              <IntegrationSavedModalBody
                syncStatuses={meetingSummarySyncStatus.length ? [meetingSummarySyncStatus[0]] : []}
                serviceId={serviceId}
                onHide={handleOnHide}
              />
            ) : (
              <AddMeetingToCRMModalBody
                roomSession={roomSession}
                serviceId={serviceId}
                isEventAppendSupported={isEventAppendSupported}
                isNotifySupported={isNotifySupported}
                sessionObject={sessionMapping}
                sessionLabel={sessionLabel}
                updateTitle={sessionTitle ?? undefined}
              />
            )}
          </FormProvider>
        }
        showFooterActions={!savedMeetingResult}
        onFooterPrimaryAction={handlePrimaryAction}
        footerPrimaryActionLoading={isLoading || accountIsLoading || isGetAiAgentResultLoading}
        onFooterSecondaryAction={onHide}
        centered={true}
      />
      <ConfirmationModal />
    </>
  )
}

interface AddMeetingToCRMModalBodyProps {
  roomSession: RoomSession
  serviceId: IntegrationServiceId
  isEventAppendSupported: boolean
  isNotifySupported: boolean
  sessionObject: string
  sessionLabel: string
  updateTitle?: string
}

const AddMeetingToCRMModalBody: React.FC<AddMeetingToCRMModalBodyProps> = ({
  roomSession,
  serviceId,
  isEventAppendSupported,
  isNotifySupported,
  sessionObject,
  sessionLabel,
  updateTitle,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { control, setValue, watch } = useFormContext<FormValues>()
  const selectedExternalUserId = watch('externalUserId')
  const initialMeetingDescription = watch('initialMeetingDescription')

  const { trackingClient } = useTracking()
  const [isModified, setIsModified] = useState<boolean>(false)
  const [isInitialized, setIsInitialized] = useState<boolean>(false)
  const [searchInput, setSearchInput] = useState<string | null>(null)

  const {
    data: contacts,
    isFetching: isContactsFetching,
    isError: isContactsError,
    error: contactsError,
  } = useGetIntegrationUsersQuery({
    accountId,
    connector: serviceId,
    filters: searchInput ? { name: searchInput } : undefined,
  })

  const [editor, setEditor] = useState<LexicalEditor | null>(null)

  const { data: additionalObjects, isFetching: isAdditionalObjectsFetching } = useGetAdditionalObjectsQuery(
    {
      accountId,
      connector: serviceId,
      relatedTo: { id: selectedExternalUserId },
      meetingStartDate: new Date(new Date(roomSession.startedAt).getTime() - EVENT_DATE_OFFSET).toISOString(),
      meetingEndDate: new Date(new Date(roomSession.finishedAt ?? roomSession.startedAt).getTime() + EVENT_DATE_OFFSET).toISOString(),
    },
    { skip: !selectedExternalUserId || !isEventAppendSupported, refetchOnMountOrArgChange: true },
  )

  const contactOptions = useMemo(() => {
    if (contacts && contacts.data) {
      return getContactOptions(contacts.data, serviceId)
    }
    return []
  }, [contacts, serviceId])

  useEffect(() => {
    if (!editor) {
      return
    }

    editor.update(() => {
      const parser = new DOMParser()
      const dom = parser.parseFromString(initialMeetingDescription, 'text/html')
      const nodes = $generateNodesFromDOM(editor, dom)
      $getRoot().select()
      $insertNodes(nodes)
    })
  }, [editor, initialMeetingDescription])

  const eventOptions = useMemo(() => {
    if (!additionalObjects) {
      return []
    }
    const config = updateTitle
      ? {
          path: sessionObject,
          idKey: 'Id',
          labelKey: updateTitle,
          household: { householdPath: 'household', eventPath: sessionObject },
        }
      : eventConfigMap[serviceId]
    if (config) {
      return extractEvents(additionalObjects, config)
    }
    return []
  }, [additionalObjects, serviceId, updateTitle, sessionObject])

  const onSearchInputChange = async (searchInput: string) => {
    setSearchInput(searchInput)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleTextContentChange = (textContent: string) => {
    if (!isModified && isInitialized) {
      setIsModified(true)
      trackingClient.track({
        eventName: TrackEvent.MEETING_SUMMARY_EDITED_IN_MODAL,
        eventDetails: {
          serviceId: serviceId,
        },
      })
    }
    if (!isInitialized) {
      setIsInitialized(true)
    }
  }

  const handleOnChange = (editorState: EditorState, editor: LexicalEditor) => {
    const htmlValue = editorState.read(() => {
      return $generateHtmlFromNodes(editor, null)
    })

    setValue('meetingDescription', htmlValue)
  }

  if (isContactsFetching && !contacts) {
    return (
      <div className='d-flex justify-content-center'>
        <Spinner />
      </div>
    )
  }

  if (isContactsError && INVALID_INTEGRATION_ERROR_MESSAGES.some((ignoredMessage) => JSON.stringify(contactsError).match(ignoredMessage))) {
    return (
      <>
        <Container className='p-0'>
          <Row>
            <Col>Your integration is invalid please reconnect your integration:</Col>
          </Row>
        </Container>
      </>
    )
  }

  return (
    <Container>
      <Row className='mt-3'>
        <Col xs={12}>
          <ContactDropdown
            fieldName='externalUserId'
            contactOptions={contactOptions}
            serviceId={serviceId}
            onSearchInputChange={onSearchInputChange}
            isLoading={isContactsFetching}
            onContactOptionSelected={() => {
              setValue('externalMeetingId', null)
            }}
          />
        </Col>
      </Row>
      {isNotifySupported && (
        <div>
          <Row className='mt-3'>
            <Col xs={12}>
              <div className='lead-extra-small'>Notify user</div>
              <Controller
                name='notifyUserId'
                control={control}
                render={({ field }) => <UserSelectDropdown serviceId={serviceId} onOptionSelected={field.onChange} />}
              />
            </Col>
          </Row>
          <Row className='mt-3'>
            <Col xs={12}>
              <div className='lead-extra-small'>Notify team</div>
              <Controller
                name='notifyTeamId'
                control={control}
                render={({ field }) => <TeamSelectDropdown serviceId={serviceId} onOptionSelected={field.onChange} />}
              />
            </Col>
          </Row>
        </div>
      )}
      {isEventAppendSupported ? (
        <Row className='mt-3'>
          <Col xs={12}>
            <div className='lead-extra-small'>{sessionLabel} (Optional)</div>
            <Controller
              name='externalMeetingId'
              control={control}
              render={({ field }) => {
                const option = field.value ? eventOptions.find((e) => e.id === field.value) : null
                return (
                  <SelectDropdown
                    initialSelectedOption={option ?? null}
                    options={eventOptions}
                    className='py-2 zocks-px-0.75'
                    onOptionSelected={(option) => field.onChange(option.id)}
                    onDeselect={() => field.onChange(null)}
                    placeholder={`Select your ${sessionLabel}`}
                    deselectOptionLabel={`Select your ${sessionLabel}`}
                    loading={isAdditionalObjectsFetching}
                    showDeselectOption={true}
                  />
                )
              }}
            />
          </Col>
        </Row>
      ) : null}
      <Row className='mt-3'>
        <Col>
          <div className='lead-extra-small'>Description</div>
        </Col>
      </Row>
      <Row className='mt-1 gy-3 zocks-bg-color-gray-100 zocks-p-0.75'>
        <Col>
          <Controller
            name='meetingDescription'
            control={control}
            render={() => (
              <WritableLexicalEditor
                ref={(editor) => setEditor(editor)}
                onChange={handleOnChange}
                onTextContentChange={handleTextContentChange}
                className='flex-column-reverse align-items-start'
                editorContainerClassName='flex-grow-1 add-meeting-to-crm-editor-container'
                containerClassName='w-100'
                lexicalNodes={LEXICAL_NODES}
                header={
                  <RichTextToolbarPlugin className='px-3 zocks-py-0.75 gap-2'>
                    <div className='editable-text-result-rich-text-divider' />
                  </RichTextToolbarPlugin>
                }
              />
            )}
          />
        </Col>
      </Row>
    </Container>
  )
}
